/* eslint-disable no-console */
/* eslint-disable camelcase */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ProfileScreen from "../../components/screens/Profile/ProfileScreen";
import ShareDrawer from "@nualang/nualang-ui-components/dist/Misc/ShareButton/ShareDrawer";
import {
  completions as completionQuerys,
  users as userQuerys,
  courses as courseQuerys,
  classrooms as classroomQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import DashboardImage from "../../img/dashboard.svg";
import MembersImage from "../../img/members.svg";
import config from "../../config";

import {
  getCourses,
  deleteCourse as deleteCourseAPI,
} from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getClassrooms } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import {
  getCourseMember,
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import {
  getClassroomMembers,
  getClassroomMember,
  createClassroomMember,
} from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { getMemberCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  courses as courseHelpers,
  classrooms as classroomHelpers,
  completions as completionHelpers,
  users as usersHelpers,
} from "../../utils/helpers";
import { useApp, useClassroom, useCourse, useSubscription } from "../../hooks";

function Profile(props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { authData, navigate, handleSignIn, authenticated, isCommunityAccessRestricted } =
    props;

  const params = useParams();
  const { profileId } = params;

  const { openSnackbar, startLoading, stopLoading } = useApp();
  const { subscription } = useSubscription();

  const { username, attributes } = authData;
  // Someone else's public courses/classrooms are community content - a
  // restricted user can still view their own profile's own public work,
  // but not browse another community member's by visiting their profile.
  const isOwnProfile = profileId === username;
  const isViewingRestrictedCommunityProfile =
    isCommunityAccessRestricted && !isOwnProfile;
  const [shareScreenOpen, setShareScreenOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const { setIsJoinCourseDialogOpen, setNewCourse } = useCourse();
  const { setIsJoinClassroomDialogOpen, setNewClassroom, memberLimit } =
    useClassroom();

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, true);
      return response;
    },
    {
      userId: profileId,
    },
    {
      enabled: !!profileId,
    },
  );

  const user = userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const userAttributes =
    userQuery.isSuccess && userQuery.data && userQuery.data.UserAttributes
      ? userQuery.data.UserAttributes
      : {};


  const coursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      return formattedCourses;
    },
    {
      filters: {
        createdBy: profileId,
        visibility: "public",
        verbose: false,
        // Get all using high limit
        limit: config.limits.courses,
      },
    },
    { enabled: !!profileId && !isViewingRestrictedCommunityProfile },
  );

  const classroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: profileId,
        visibility: "public",
        verbose: false,
        // Get all using high limit
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!profileId && !isViewingRestrictedCommunityProfile },
  );

  const memberCompletionsQuery = completionQuerys.useMemberCompletions(
    async (memberId) => {
      const memberCompletions = await getMemberCompletions(memberId);
      const formattedCompletions = await completionHelpers.formatCompletions(
        memberCompletions.Items,
        memberId,
        attributes.preferred_username,
      );
      return formattedCompletions;
    },
    {
      memberId: username,
    },
    { enabled: !!username },
  );

  const isLoading =
    memberCompletionsQuery.isLoading ||
    coursesQuery.isLoading ||
    classroomsQuery.isLoading;

  // Mutations
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      navigate(`/courses/${variables.courseId}`);
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const deleteCourseMutation = courseQuerys.useDeleteCourse(deleteCourseAPI, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: () => {
      openSnackbar("course_deleted", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: profileId,
          visibility: "public",
          verbose: false,
          limit: config.limits.courses,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem_deleting", "error");
    },
  });

  const joinClassroomMutation = classroomQuerys.useJoinClassroom(
    createClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("joined_classroom", "success");
        navigate(`/classrooms/${variables.classroomId}`);
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const handleViewCourses = () => navigate("/courses");
  const handleViewClassrooms = () => navigate("/classrooms");
  const handleSearch = isCommunityAccessRestricted ? undefined : () => navigate("/search");
  const handleCreateCourse = () => navigate("/courses/create");
  const handleSearchClassrooms = isCommunityAccessRestricted
    ? undefined
    : () => navigate("/classrooms/search");
  const handleCreateClassroom = () => navigate("/classrooms/create");

  const handleJoinClassroom = (classroom, memberCount) => {
    if (authenticated) {
      setNewClassroom(classroom);
      if (memberCount >= memberLimit) {
        openSnackbar("classroom_member_limit_reached", "error");
      } else if (classroom.visibility === "private") {
        setIsJoinClassroomDialogOpen(true);
      } else {
        startLoading();
        joinClassroomMutation.mutate({
          classroomId: classroom.classroomId,
          username,
        });
      }
    } else {
      handleSignIn();
    }
  };

  const handleStartCourse = async (course) => {
    if (!username) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId: course.courseId, username });
      }
    }
  };

  const handleLeaveCourse = (courseId) => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const handleDeleteCourse = (courseId) => {
    startLoading();
    deleteCourseMutation.mutate({ courseId, username });
  };

  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleViewCourseTopic = (cId, sectionId, topicId) => {
    navigate(`/courses/${cId}/${sectionId}/${topicId}`);
  };

  const handleViewClassroom = (classroomId) => {
    navigate(`/classrooms/${classroomId}`);
  };

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);

  const handleViewUserProfile = (createdBy) => {
    navigate(`/profiles/${createdBy}`);
  };

  return (
    <>
      <ProfileScreen
        t={t}
        name={attributes.preferred_username}
        handleViewCourses={handleViewCourses}
        handleViewClassrooms={handleViewClassrooms}
        handleSearch={handleSearch}
        handleCreateCourse={handleCreateCourse}
        handleSearchClassrooms={handleSearchClassrooms}
        handleCreateClassroom={handleCreateClassroom}
        imageUrl={DashboardImage}
        memberPlaceholderImageUrl={MembersImage}
        handleJoinClassroom={handleJoinClassroom}
        memberCompletions={
          memberCompletionsQuery.isSuccess &&
          Array.isArray(memberCompletionsQuery.data) &&
          memberCompletionsQuery.data.length
            ? memberCompletionsQuery.data
            : []
        }
        completions={
          memberCompletionsQuery.isSuccess &&
          Array.isArray(memberCompletionsQuery.data) &&
          memberCompletionsQuery.data.length
            ? memberCompletionsQuery.data
            : []
        }
        handleViewCourseTopic={handleViewCourseTopic}
        handleStartCourse={handleStartCourse}
        handleLeaveCourse={handleLeaveCourse}
        handleDeleteCourse={handleDeleteCourse}
        handleViewCourse={handleViewCourse}
        handleViewClassroom={handleViewClassroom}
        getMemberDetails={getMemberDetails}
        getCourseSections={getCourseSections}
        memberId={username}
        getCourseMember={getCourseMember}
        getCourseMembers={getCourseMembers}
        getClassroomMembers={getClassroomMembers}
        getClassroomMember={getClassroomMember}
        username={username}
        navigate={navigate}
        membersProps={{
          handleViewActivity,
        }}
        handleViewUserProfile={handleViewUserProfile}
        isLoading={isLoading}
        courses={
          coursesQuery.isSuccess &&
          Array.isArray(coursesQuery.data) &&
          coursesQuery.data.length
            ? coursesQuery.data
            : []
        }
        classrooms={
          classroomsQuery.isSuccess &&
          Array.isArray(classroomsQuery.data) &&
          classroomsQuery.data.length
            ? classroomsQuery.data
            : []
        }
        profileTitle={
          userAttributes &&
          userAttributes.preferred_username &&
          userAttributes.preferred_username
            ? userAttributes.preferred_username
            : ""
        }
        profileSubheader={
          user && user.UserCreateDate
            ? `Joined ${DateTime.fromISO(user.UserCreateDate).toLocaleString(DateTime.DATE_FULL)}`
            : ""
        }
        profilePicture={
          userAttributes && userAttributes.picture ? userAttributes.picture : ""
        }
        verificationStatus={
          user.verificationStatus ? user.verificationStatus : ""
        }
        gameAward={user.gameAward ? user.gameAward : ""}
      />
      <ShareDrawer
        t={t}
        url={shareUrl}
        open={shareScreenOpen}
        handleClose={() => setShareScreenOpen(false)}
        subscription={subscription}
      />
    </>
  );
}

Profile.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  isCommunityAccessRestricted: PropTypes.bool,
};

export default Profile;
